// @story: none
import { Component, ChangeDetectionStrategy, computed, effect, inject } from '@angular/core';
import { toObservable, toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgComponentOutlet } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { Dialog } from '@angular/cdk/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { EMPTY, filter, switchMap, take, timer } from 'rxjs';
import { format, startOfWeek } from 'date-fns';
import {
  PageLayout,
  SidebarNavComponent,
  SidebarNavStateService,
  LanguageService,
  SupportedLanguage,
  ThemeService,
  ToastContainerComponent,
  ZagIcon,
  NavConfigEntry,
  isNavSection,
  TopBarComponent,
  SidebarProfileComponent,
  ProfileUser,
  BreadcrumbService,
  PageActionsOutletDirective,
  PageActionsService,
  Avatar,
} from '@zag/ui';
import { AuthService, PermissionService, UsersMeService } from '@zag/auth/ui';
import { NotificationsService } from '@zag/notifications/ui';
import { applyApprovalsBadge, filterNavEntries, NAV_CONFIG, NAV_FEATURE_MAP } from './navigation.config';
import { ApprovalsCountService } from './approvals-count.service';
import { ClockStampButtonComponent } from './components/clock-stamp/clock-stamp-button.component';
import { DashboardService } from '@zag/core/ui';
import { ACTIVE_LICENCES_DIALOG_CONFIG, ActiveLicencesDialogComponent, FeatureAccessClientService } from '@zag/features/ui';
import { TestFeedbackSidebarComponent } from './verification/test-feedback-sidebar.component';
import { isIssueVerificationHost } from './verification/test-feedback.service';
// Dialog components are lazy-loaded via dynamic import() to keep them out of the initial bundle.

/**
 * Main application layout component.
 * Uses Flexbox to create a full-height sidebar with content area.
 *
 * Layout structure (horizontal flexbox):
 * - Sidebar on the left (auto width based on content)
 * - Content area on the right (flex: 1), split vertically:
 *   - Top bar
 *   - Page header bar (breadcrumbs + page actions)
 *   - Main content (router outlet)
 *
 * This component is only used for authenticated users with normal access.
 * Access control (pending, denied, error states) is handled via separate routes.
 */
@Component({
  selector: 'sc-layout',
  imports: [
    RouterModule,
    TranslatePipe,
    PageLayout,
    SidebarNavComponent,
    ToastContainerComponent,
    ZagIcon,
    TopBarComponent,
    SidebarProfileComponent,
    PageActionsOutletDirective,
    Avatar,
    ClockStampButtonComponent,
    NgComponentOutlet,
    TestFeedbackSidebarComponent,
  ],
  template: `
    @if (isSigningOut()) {
      <div class="app-signing-out" aria-busy="true" aria-live="polite"></div>
    } @else {
      <div class="app-layout">
        <!-- Sidebar -->
        @if (currentUser() && hasSidebarContent()) {
          <zag-sidebar-nav class="app-sidebar" [entries]="navConfig()" [showSettings]="canAccessSettings()">
            <!-- Profile section at sidebar bottom -->
            <zag-sidebar-profile
              slot="profile"
              [user]="profileUser()"
              [collapsed]="navState.isCollapsed()"
              [currentLanguage]="currentLanguage()"
              [currentTheme]="currentTheme()"
              [unreadCount]="unreadCount()"
              (signOut)="onSignOut()"
              (navigateToProfile)="onNavigateToProfile()"
              (versionInfoClick)="openActiveLicencesDialog()"
            />
          </zag-sidebar-nav>
        }

        <!-- Content area (vertical flexbox) -->
        <div class="app-content-area">
          <!-- Page Actions Bar: DOM-first so the Save button (rendered via
               zagPageActionsOutlet) is the first matching element for
               button:has-text("Save") queries, even when the current entity's
               name contains "Save". CSS order: 1 keeps it visually last. -->
          @if (hasPageActions()) {
            <div class="app-page-footer">
              <div class="app-page-footer__actions" zagPageActionsOutlet></div>
            </div>
          }

          <!-- Main Content Area -->
          <main class="app-content">
            <zag-page-layout [padding]="null" class="app-page-layout">
              <router-outlet />
            </zag-page-layout>
          </main>

          <!-- Top Bar (with breadcrumbs and collapsible search): DOM-last,
               CSS order: -1 keeps it visually first. -->
          <header class="app-topbar">
            <zag-top-bar [breadcrumbs]="breadcrumbs()" [showAIAction]="canOpenAIAssistant()" (aiChat)="onOpenAIChat()">
              <!-- Quick-stamp button (F12): permanent, right of the AI assistant,
                   only for users with the self-service timetracking permission. -->
              @if (canUseQuickStamp()) {
                <sc-clock-stamp-button slot="after-ai" />
              }
            </zag-top-bar>
          </header>
        </div>

        <!-- Mobile Bottom Navigation -->
        @if (currentUser()) {
          <nav
            class="app-mobile-bottom-nav"
            [style.grid-template-columns]="mobileNavGridColumns()"
            [attr.aria-label]="'navigation.mainNavigation' | translate"
          >
            <!-- Menu button -->
            <button
              type="button"
              class="app-mobile-bottom-nav__action"
              (click)="openMobileMenuDialog()"
              [attr.aria-label]="'navigation.openMenu' | translate"
              [attr.title]="'navigation.openMenu' | translate"
            >
              <span zagIcon="menu"></span>
            </button>

            <!-- Dashboard button -->
            @if (canAccessDashboard()) {
              <button
                type="button"
                class="app-mobile-bottom-nav__action"
                (click)="navigateToDashboard()"
                [attr.aria-label]="'navigation.dashboard' | translate"
                [attr.title]="'navigation.dashboard' | translate"
              >
                <span zagIcon="dashboard"></span>
              </button>
            }

            <!-- Calendar button -->
            <button
              type="button"
              class="app-mobile-bottom-nav__action"
              (click)="navigateToCalendar()"
              [attr.aria-label]="'navigation.calendar' | translate"
              [attr.title]="'navigation.calendar' | translate"
            >
              <span zagIcon="calendar_today"></span>
            </button>

            <!-- Stamp button replaces the AI assistant in the footer when
                 available; the AI assistant moves into the menu dialog then. -->
            @if (showStampInFooter()) {
              <sc-clock-stamp-button [showOnSmallScreens]="true" />
            } @else if (canOpenAIAssistant()) {
              <button
                type="button"
                class="app-mobile-bottom-nav__action"
                (click)="onOpenAIChat()"
                [attr.aria-label]="'ai.chat' | translate"
                [attr.title]="'ai.chat' | translate"
              >
                <span zagIcon="auto_awesome"></span>
              </button>
            }

            <!-- Profile button - Opens profile dialog -->
            <button
              type="button"
              class="app-mobile-bottom-nav__action"
              (click)="openProfileDialog()"
              [attr.aria-label]="'navigation.userMenu' | translate"
              [attr.title]="'navigation.userMenu' | translate"
            >
              <zag-avatar [name]="profileUser().name" [src]="profileUser().avatarUrl" size="md" />
            </button>
          </nav>
        }

        <!-- Toast Notifications -->
        <zag-toast-container />

        <!-- Project stopwatch pill (spec project-time-tracking, story 7):
             floats above EVERY screen while a timer runs. Lazy-loaded via
             NgComponentOutlet so @zag/projects/ui stays out of the initial
             bundle (the lib is lazy-routed — static imports are forbidden);
             the component itself restores the server-side timer and hides
             while idle. -->
        @if (projectTimerPill(); as pill) {
          <ng-container *ngComponentOutlet="pill; inputs: projectTimerPillInputs" />
        }

        <!-- Test feedback sidebar — issue verification instances only (AC 1/2) -->
        @if (isIssueInstance) {
          <sc-test-feedback-sidebar />
        }
      </div>
    }
  `,
  styles: [
    `
      /* Blank, full-bleed placeholder while signing out — keeps the
       * background colour consistent with /login to avoid a flash. */
      .app-signing-out {
        position: fixed;
        inset: 0;
        background: var(--background-color-secondary);
      }

      /* Main layout: horizontal flexbox (sidebar | content) */
      .app-layout {
        display: flex;
        flex-direction: row;
        height: 100vh;
        height: 100svh; /* Small viewport height for mobile browsers */
        overflow: hidden;
        background: var(--background-color-secondary);
        box-sizing: border-box;
      }

      /* Sidebar: auto width, handled by the component itself */
      .app-sidebar {
        flex-shrink: 0;
      }

      /* Content area: vertical flexbox (topbar | page-header | content) */
      .app-content-area {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-width: 0;
        padding: var(--spacing-md);
        padding-left: var(--spacing-md);
        gap: var(--spacing-md);
      }

      /* Top bar — order: -1 keeps it visually first even though it is last in DOM */
      .app-topbar {
        display: flex;
        align-items: center;
        gap: var(--spacing-md);
        flex-shrink: 0;
        height: auto;
        order: -1;
      }

      .app-topbar zag-top-bar {
        flex: 1;
      }

      /* Page Actions Bar (only page actions, no breadcrumbs - those are in the topbar)
         order: 1 keeps it visually last even though it is first in DOM */
      .app-page-footer {
        position: relative;
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        flex-shrink: 0;
        min-height: 36px;
        justify-content: flex-end;
        order: 1;
      }

      .app-page-footer::after {
        content: ' ';
        position: absolute;
        top: calc(var(--spacing-md) * -1);

        @media (max-width: 539px) {
          top: calc(var(--spacing-sm) * -1);
        }
        left: 0;
        right: 0;
        height: 1px;
        background: var(--border-color-default);
        opacity: 0.5;
      }

      .app-page-footer__actions {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        flex-wrap: nowrap;
        justify-content: flex-start;
      }

      /* Main content */
      .app-content {
        flex: 1;
        min-height: 0;
        overflow: hidden;
        border-radius: var(--border-radius-md);
      }

      /* Set CSS custom property hooks on the PageLayout host.
       * CSS custom properties cascade naturally through the DOM — they reach the
       * inner container without ::ng-deep. */
      .app-page-layout {
        --zag-page-layout-height: 100%;
        --zag-page-layout-min-height: 0;
        --zag-page-layout-min-width: 0;
        --zag-page-layout-overflow: hidden;
        --zag-page-layout-container-height: 100%;
        --zag-page-layout-container-min-height: 0;
        --zag-page-layout-container-min-width: 0;
        --zag-page-layout-container-overflow: auto;
        --zag-page-layout-container-padding: 0;
      }

      @media print {
        .app-layout {
          display: block;
          height: auto;
          overflow: visible;
          background: var(--surface-color-default);
        }

        .app-sidebar,
        .app-topbar,
        .app-page-footer,
        .app-mobile-bottom-nav,
        zag-toast-container {
          display: none !important;
        }

        .app-content-area,
        .app-content {
          display: block;
          height: auto;
          min-height: 0;
          overflow: visible;
          padding: 0;
        }

        .app-page-layout {
          --zag-page-layout-height: auto;
          --zag-page-layout-overflow: visible;
          --zag-page-layout-container-height: auto;
          --zag-page-layout-container-overflow: visible;
          --zag-page-layout-container-padding: 0;
        }
      }

      .app-mobile-bottom-nav {
        display: none;
      }

      @media (max-width: 539px) {
        .app-layout {
          flex-direction: column;
          padding: 0 var(--spacing-xs);
        }

        .app-sidebar {
          display: none;
        }

        .app-content-area {
          padding: 0;
          gap: 0;
          padding-bottom: calc(60px + env(safe-area-inset-bottom));
          overflow: hidden;
        }

        /* Topbar is visible on mobile (contains breadcrumbs + search trigger) */
        .app-topbar {
          min-height: 40px;
        }

        .app-content {
          overflow: auto;
          -webkit-overflow-scrolling: touch;
        }

        /* Mobile overrides — reuse the same custom properties but with mobile-specific
         * values. --page-layout-height/overflow are set by child route components. */
        .app-page-layout {
          --zag-page-layout-height: var(--page-layout-height, auto);
          --zag-page-layout-min-height: 0;
          --zag-page-layout-overflow: var(--page-layout-overflow, visible);
          --zag-page-layout-container-height: var(--page-layout-container-height, auto);
          --zag-page-layout-container-overflow: var(--page-layout-container-overflow, visible);
          /* Reset padding override — let the [padding] input take effect on mobile */
          --zag-page-layout-container-padding: initial;
        }

        .app-mobile-bottom-nav {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: calc(var(--z-index-modal) - 1);
          display: grid;
          align-items: center;
          gap: 0;
          min-height: 52px;
          padding: 0;
          padding-bottom: env(safe-area-inset-bottom);
          background: var(--background-color-elevated);
          border-top: 1px solid var(--border-color-default);
          box-shadow: var(--shadow-md);
        }

        /* Grid columns set dynamically via [style.grid-template-columns] */

        .app-mobile-bottom-nav > * {
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          height: 52px;
          overflow: hidden;
        }

        .app-mobile-bottom-nav__action {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 52px;
          border: none;
          border-radius: 0;
          background: transparent;
          color: var(--text-color-secondary);
          cursor: pointer;
          margin: 0;
          padding: 0;

          &:hover {
            background: var(--surface-color-hover);
            color: var(--text-color-primary);
          }

          span[zagIcon] {
            font-size: 24px;
          }
        }
      }

      /* Print: collapse the shell to just the page content. The user
       * triggers print with the browser's print preview; the planning
       * grid is the only payload that benefits from a paper view. The
       * planning-period-edit page and planning-grid component apply
       * further per-component rules.
       *
       * The flex chain (.app-layout → .app-content-area → .app-content
       * → .app-page-layout → page → planning-grid) is full of
       * 'flex: 1; min-height: 0' rules that, once 'height: 100vh' is
       * relaxed for print, expand to fill the unbounded print canvas
       * with empty space (causing dozens of trailing blank pages).
       * Override every link in the chain to 'height: auto;
       * min-height: 0; max-height: none; overflow: visible' so each
       * box collapses to its content. */
      @media print {
        .app-layout,
        .app-content-area,
        .app-content,
        .app-page-layout {
          display: block;
          height: auto;
          max-height: none;
          min-height: 0;
          overflow: visible;
          flex: none;
          background: #fff;
        }

        .app-sidebar,
        .app-topbar,
        .app-page-footer,
        .app-mobile-bottom-nav {
          display: none !important;
        }

        .app-content-area {
          padding: 0;
          gap: 0;
        }

        .app-content {
          border-radius: 0;
        }

        .app-page-layout {
          --zag-page-layout-height: auto;
          --zag-page-layout-min-height: 0;
          --zag-page-layout-overflow: visible;
          --zag-page-layout-container-height: auto;
          --zag-page-layout-container-min-height: 0;
          --zag-page-layout-container-overflow: visible;
          --zag-page-layout-container-padding: 0;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent {
  private readonly authService = inject(AuthService);
  private readonly usersMeService = inject(UsersMeService);
  private readonly languageService = inject(LanguageService);
  private readonly themeService = inject(ThemeService);
  private readonly permissionService = inject(PermissionService);
  private readonly entitlementsService = inject(FeatureAccessClientService);
  protected readonly navState = inject(SidebarNavStateService);
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly pageActionsService = inject(PageActionsService);
  private readonly notificationsService = inject(NotificationsService);
  private readonly approvalsCountService = inject(ApprovalsCountService);
  private readonly dialog = inject(Dialog);
  private readonly router = inject(Router);
  private readonly dashboardService = inject(DashboardService);

  /** Badge refresh cadence while the nav is visible (story 34-1) — gentle poll, ≤ 60 s. */
  private static readonly APPROVALS_POLL_MS = 60_000;

  constructor() {
    // Approvals badge (34-1): initial load + gentle poll — ONLY while this
    // layout (= the nav) is alive AND the user carries the queue permission.
    // switchMap tears the timer down when the permission flips; the
    // destroyRef-bound subscription prevents timer leaks on logout.
    toObservable(this.canSeeApprovals)
      .pipe(
        switchMap(canSee => (canSee ? timer(0, LayoutComponent.APPROVALS_POLL_MS) : EMPTY)),
        takeUntilDestroyed()
      )
      .subscribe(() => this.approvalsCountService.refresh());

    // Enable team-assignment check for employees so the nav can hide my-plan
    // when the user has no current team assignment.
    effect(() => {
      if (this.permissionService.hasRole('employee')) {
        this.dashboardService.enableMyTeamCheck();
      }
    });
  }

  // Whether the page actions bar should be shown (only when a page provides actions)
  readonly hasPageActions = this.pageActionsService.hasPortal;

  // Unread notification count for the sidebar profile badge
  readonly unreadCount = this.notificationsService.unreadCount;

  readonly supportedLanguages: SupportedLanguage[] = ['en', 'de', 'fr', 'it'];

  // Breadcrumbs from service
  readonly breadcrumbs = this.breadcrumbService.breadcrumbs;

  // Approvals badge visibility (story 34-1): mirrors the /approvals route guard.
  readonly canSeeApprovals = computed(() => this.permissionService.hasPermission('timetracking:review-queue:read'));

  // Own time tracking gate (story 35-2, U6): "Deine Woche" requires a linked
  // person, not a role — mirrors the stamp-button gate in its component.
  protected readonly hasLinkedPerson = computed(() => this.usersMeService.profile()?.linkedPerson?.id !== undefined);

  // Navigation configuration - filtered by permissions, feature flags, team assignment, with badge applied
  readonly navConfig = computed(() =>
    applyApprovalsBadge(
      this.filterEntriesByTeam(
        this.filterEntriesByFeature(
          filterNavEntries(NAV_CONFIG, {
            hasRole: role => this.permissionService.hasRole(role),
            hasPermission: permission => this.permissionService.hasPermission(permission),
            hasAnyPermission: permissions => this.permissionService.hasAnyPermission(permissions),
            hasLinkedPerson: this.hasLinkedPerson(),
          })
        )
      ),
      this.approvalsCountService.count()
    )
  );

  // Settings access - controls visibility of settings link in sidebar
  readonly canAccessSettings = computed(() => this.permissionService.canAccessSettings());

  // Sidebar content check - hide sidebar if user has no nav entries and no settings access
  readonly hasSidebarContent = computed(() => this.navConfig().length > 0 || this.canAccessSettings());

  // Language configuration
  readonly currentLanguage = this.languageService.currentLanguage;

  // Theme configuration
  readonly currentTheme = this.themeService.currentTheme;

  // Expose auth state as computed signals for template
  readonly currentUser = computed(() => this.authService.currentUser());

  // Static per-pageload host check — true only on issue-<n>.issues.* instances.
  // Evaluated without touching TestFeedbackService so no verification code
  // or network call runs on other environments (AC 2).
  readonly isIssueInstance = isIssueVerificationHost();

  // Hides layout during sign-out so the user does not see
  // half-rendered chrome while the route swaps to /login.
  readonly isSigningOut = this.authService.isSigningOut;

  // Profile user data for sidebar profile component
  readonly profileUser = computed((): ProfileUser => {
    const user = this.currentUser();
    return {
      name: user?.name || user?.email || 'User',
      email: user?.email,
      avatarUrl: this.usersMeService.profile()?.image ?? undefined,
    };
  });

  protected readonly canOpenAIAssistant = computed(
    () => !!this.currentUser() && this.entitlementsService.featuresLoaded() && this.entitlementsService.hasFeature('ai-assistant')
  );

  // Quick-stamp button visibility (F12): any authenticated user with the
  // self-service timetracking permission — the component itself additionally
  // requires a linked person.
  protected readonly canUseQuickStamp = computed(
    () =>
      this.entitlementsService.featuresLoaded() &&
      this.entitlementsService.hasFeature('timetracking') &&
      this.permissionService.hasPermission('timetracking:my-week:read')
  );

  // Project stopwatch pill waits for loaded entitlements before its mount-time restore call.
  readonly canUseProjectTimer = computed(
    () =>
      this.permissionService.hasPermission('projects:my-time:record') &&
      this.entitlementsService.featuresLoaded() &&
      this.entitlementsService.hasFeature('projects')
  );

  // Lazily imported pill component — resolves once when the gate first opens.
  readonly projectTimerPill = toSignal(
    toObservable(this.canUseProjectTimer).pipe(
      filter(Boolean),
      take(1),
      switchMap(() => import('@zag/projects/ui').then(m => m.ProjectTimerPillComponent))
    ),
    { initialValue: null }
  );

  /** Stopping from the pill navigates to the capture screen. */
  readonly projectTimerPillInputs = { captureLink: '/projects' };

  // Dashboard access check for mobile bottom nav
  readonly canAccessDashboard = computed(() => this.permissionService.hasPermission('dashboard:read'));

  // Navigation entries for mobile menu dialog.
  // Primary items dashboard + calendars are excluded since they have their own bottom-nav buttons.
  readonly mobileMenuNavEntries = computed(() => this.filterOutPrimaryItems(this.navConfig(), ['dashboard', 'calendars']));

  // Linked person included so the footer never holds an empty slot.
  protected readonly showStampInFooter = computed(() => this.canUseQuickStamp() && this.hasLinkedPerson());

  // Whether the AI assistant moved into the menu dialog (stamp took its footer slot).
  protected readonly showAIAssistantInMenu = computed(() => this.canOpenAIAssistant() && this.showStampInFooter());

  // Dynamic grid columns for mobile bottom nav based on visible buttons
  readonly mobileNavGridColumns = computed(() => {
    let count = 3; // Menu + Calendar + Profile are always shown
    if (this.canAccessDashboard()) count++;
    if (this.showStampInFooter() || this.canOpenAIAssistant()) count++;
    return `repeat(${count}, minmax(0, 1fr))`;
  });

  async onSignOut(): Promise<void> {
    await this.authService.confirmSignOut();
  }

  async onNavigateToProfile(): Promise<void> {
    await this.router.navigate(['/profile']);
  }

  /**
   * Open the AI chat dialog (lazy-loaded to reduce initial bundle size).
   */
  async onOpenAIChat(): Promise<void> {
    const { AIChatDialogComponent } = await import('./components/ai-chat/ai-chat-dialog.component');
    this.dialog.open(AIChatDialogComponent, {
      width: 'min(96vw, 720px)',
      maxWidth: '96vw',
      maxHeight: '96svh',
      panelClass: 'ai-chat-dialog-panel',
      backdropClass: 'ai-chat-dialog-backdrop',
    });
  }

  async navigateToDashboard(): Promise<void> {
    await this.router.navigate(['/dashboard']);
  }

  async navigateToCalendar(): Promise<void> {
    const date = format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd');
    await this.router.navigate(['/calendars'], { queryParams: { view: 'week', date } });
  }

  async openMobileMenuDialog(): Promise<void> {
    const { MobileMenuDialogComponent } = await import('./components/mobile-menu-dialog/mobile-menu-dialog.component');
    const dialogRef = this.dialog.open(MobileMenuDialogComponent, {
      width: 'min(96vw, 720px)',
      maxWidth: '96vw',
      maxHeight: '96svh',
      panelClass: 'ai-chat-dialog-panel',
      backdropClass: 'ai-chat-dialog-backdrop',
      data: {
        navEntries: this.mobileMenuNavEntries(),
        showSettings: this.canAccessSettings(),
        showAIAssistant: this.showAIAssistantInMenu(),
      },
    });

    dialogRef.closed.subscribe(async result => {
      if (result && typeof result === 'object' && 'action' in result) {
        const typedResult = result as {
          action: string;
        };

        if (typedResult.action === 'openAI') {
          await this.onOpenAIChat();
        }
      }
    });
  }

  async openProfileDialog(): Promise<void> {
    const { ProfileDialogComponent } = await import('./components/profile-dialog/profile-dialog.component');
    const dialogRef = this.dialog.open(ProfileDialogComponent, {
      width: 'min(96vw, 720px)',
      maxWidth: '96vw',
      maxHeight: '96svh',
      panelClass: 'ai-chat-dialog-panel',
      backdropClass: 'ai-chat-dialog-backdrop',
      data: {
        user: this.profileUser(),
        unreadCount: this.unreadCount(),
      },
    });

    // Handle dialog result
    dialogRef.closed.subscribe(async result => {
      if (result && typeof result === 'object' && 'action' in result) {
        const typedResult = result as {
          action: string;
        };

        if (typedResult.action === 'signOut') {
          await this.onSignOut();
        }
      }
    });
  }

  protected openActiveLicencesDialog(): void {
    this.dialog.open(ActiveLicencesDialogComponent, {
      ...ACTIVE_LICENCES_DIALOG_CONFIG,
      data: { licences: this.entitlementsService.licences() ?? [] },
    });
  }

  /**
   * Hides the `my-plan` nav entry when the employee has no valid team assignment today.
   * Fails open while the team check is loading or has not yet been enabled.
   */
  private filterEntriesByTeam(entries: NavConfigEntry[]): NavConfigEntry[] {
    if (this.dashboardService.hasValidTeam()) return entries;
    return entries.filter(entry => isNavSection(entry) || entry.id !== 'my-plan');
  }

  /**
   * Hides nav entries of unlicensed modules (NAV_FEATURE_MAP). Feature-gated
   * entries stay hidden until entitlements resolve, avoiding a brief display
   * of modules the tenant cannot use.
   */
  private filterEntriesByFeature(entries: NavConfigEntry[]): NavConfigEntry[] {
    const isLicensed = (id: string | undefined): boolean => {
      const feature = id ? NAV_FEATURE_MAP[id] : undefined;
      return feature === undefined || (this.entitlementsService.featuresLoaded() && this.entitlementsService.hasFeature(feature));
    };
    return entries
      .map(entry => {
        if ('items' in entry && Array.isArray(entry.items)) {
          const items = entry.items.filter(item => isLicensed(item.id));
          return items.length > 0 ? { ...entry, items } : undefined;
        }
        return isLicensed((entry as { id?: string }).id) ? entry : undefined;
      })
      .filter((entry): entry is NavConfigEntry => entry !== undefined);
  }

  /**
   * Remove specific primary item IDs from the entries for the mobile menu dialog.
   * These items (dashboard, calendars) have dedicated bottom-nav buttons on mobile.
   */
  private filterOutPrimaryItems(entries: NavConfigEntry[], excludeIds: string[]): NavConfigEntry[] {
    return entries.filter(entry => {
      if (isNavSection(entry)) {
        return true; // Keep all sections
      }
      return !excludeIds.includes(entry.id);
    });
  }
}
